import React from 'react';
import { clsx } from 'clsx';

interface Column<T = any> {
  key: string;
  title: string;
  dataIndex?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  sorter?: boolean | ((a: T, b: T) => number);
  fixed?: 'left' | 'right';
}

interface TableProps<T = any> {
  columns: Column<T>[];
  dataSource: T[];
  rowKey?: string | ((record: T) => string);
  loading?: boolean;
  pagination?: boolean | {
    current: number;
    pageSize: number;
    total: number;
    showSizeChanger?: boolean;
    pageSizeOptions?: string[];
    onChange: (page: number, pageSize?: number) => void;
    onShowSizeChange?: (current: number, size: number) => void;
  };
  onRow?: (record: T, index?: number) => {
    onClick?: (event: React.MouseEvent) => void;
    onDoubleClick?: (event: React.MouseEvent) => void;
    [key: string]: any;
  };
  scroll?: {
    x?: string | number;
    y?: string | number;
  };
  size?: 'small' | 'middle' | 'large';
  className?: string;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
}

const Table = <T extends Record<string, any>>({
  columns,
  dataSource,
  rowKey = 'id',
  loading = false,
  pagination = false,
  onRow,
  scroll,
  size = 'middle',
  className,
  onSort,
  sortConfig: externalSortConfig
}: TableProps<T>) => {
  const [internalSortConfig, setInternalSortConfig] = React.useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Use external sort config if provided (for server-side sorting), otherwise use internal
  const sortConfig = externalSortConfig || internalSortConfig;

  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(record);
    }
    return record[rowKey] || index.toString();
  };

  const handleSort = (column: Column<T>) => {
    if (!column.sorter) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === column.key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    if (onSort) {
      // Server-side sorting
      onSort(column.key, direction);
    } else {
      // Client-side sorting
      setInternalSortConfig({ key: column.key, direction });
    }
  };

  const sortedData = React.useMemo(() => {
    // If onSort is provided, server handles sorting, so return data as-is
    if (onSort) return dataSource;
    
    if (!sortConfig) return dataSource;

    const { key, direction } = sortConfig;
    const column = columns.find(col => col.key === key);
    
    if (!column?.sorter) return dataSource;

    const sorted = [...dataSource].sort((a, b) => {
      if (typeof column.sorter === 'function') {
        return column.sorter(a, b);
      }

      const aValue = column.dataIndex ? a[column.dataIndex] : a[key];
      const bValue = column.dataIndex ? b[column.dataIndex] : b[key];

      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    });

    return direction === 'desc' ? sorted.reverse() : sorted;
  }, [dataSource, sortConfig, columns, onSort]);

  const sizeClasses = {
    small: 'text-xs',
    middle: 'text-sm',
    large: 'text-base'
  };

  const cellPadding = {
    small: 'px-2 py-1',
    middle: 'px-4 py-3',
    large: 'px-6 py-4'
  };

  const getCellValue = (record: T, column: Column<T>, index: number) => {
    if (column.render) {
      const value = column.dataIndex ? record[column.dataIndex] : record[column.key];
      return column.render(value, record, index);
    }
    return column.dataIndex ? record[column.dataIndex] : record[column.key];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderPagination = () => {
    if (!pagination || typeof pagination !== 'object') return null;

    const { current, pageSize, total, showSizeChanger = true, pageSizeOptions = ['10', '20', '50', '100'] } = pagination;
    const totalPages = Math.ceil(total / pageSize);
    
    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        pagination.onChange(page, pageSize);
      }
    };

    const handlePageSizeChange = (newPageSize: number) => {
      if (pagination.onShowSizeChange) {
        pagination.onShowSizeChange(1, newPageSize);
      } else {
        pagination.onChange(1, newPageSize);
      }
    };

    const getPageNumbers = () => {
      const pages = [];
      const showPages = 5; // Number of page numbers to show
      let start = Math.max(1, current - Math.floor(showPages / 2));
      let end = Math.min(totalPages, start + showPages - 1);
      
      if (end - start < showPages - 1) {
        start = Math.max(1, end - showPages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-700">
            Showing {total === 0 ? 0 : (current - 1) * pageSize + 1} to{' '}
            {Math.min(current * pageSize, total)} of {total} results
          </div>
          {showSizeChanger && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {pageSizeOptions.map(option => (
                  <option key={option} value={option}>{option} per page</option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={current <= 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(current - 1)}
            disabled={current <= 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {getPageNumbers().map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={clsx(
                'px-3 py-1 text-sm border rounded hover:bg-gray-50',
                page === current 
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
                  : 'border-gray-300'
              )}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(current + 1)}
            disabled={current >= totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={current >= totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={clsx('overflow-hidden', className)}>
      <div className={clsx(
        'overflow-auto',
        scroll?.x && 'overflow-x-auto',
        scroll?.y && 'overflow-y-auto'
      )}
      style={{
        maxHeight: scroll?.y,
        maxWidth: scroll?.x
      }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    cellPadding[size],
                    sizeClasses[size],
                    'font-medium text-gray-900 tracking-wider',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.sorter && 'cursor-pointer hover:bg-gray-100 select-none'
                  )}
                  style={{ width: column.width }}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.title}</span>
                    {column.sorter && (
                      <span className="ml-2">
                        {sortConfig?.key === column.key ? (
                          sortConfig.direction === 'asc' ? (
                            <span>↑</span>
                          ) : (
                            <span>↓</span>
                          )
                        ) : (
                          <span className="text-gray-400">↕</span>
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={clsx(cellPadding[size], 'text-center text-gray-500')}
                >
                  No data
                </td>
              </tr>
            ) : (
              sortedData.map((record, index) => {
                const rowProps = onRow?.(record, index);
                return (
                  <tr
                    key={getRowKey(record, index)}
                    className={clsx(
                      'hover:bg-gray-50 transition-colors',
                      rowProps?.onClick && 'cursor-pointer'
                    )}
                    {...rowProps}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={clsx(
                          cellPadding[size],
                          sizeClasses[size],
                          'text-gray-900',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right'
                        )}
                        style={{ width: column.width }}
                      >
                        {getCellValue(record, column, index)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {renderPagination()}
    </div>
  );
};

export default Table;
export type { Column, TableProps }; 